import { calculateResalePricing, type ResalePassenger } from "../../../server/src/lib/resalePricing";
import type { ChildCost } from "../../../web/src/lib/childPricing";
import { Button, Tooltip } from "antd";
import { CopyPlusIcon, DownloadIcon, FileTextIcon, PencilIcon, SendIcon } from "lucide-react";
import { computeMarkupBreakdown, draftToMarkup, type MarkupDraft } from "../lib/quoteMarkup";

const formatMoney = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency, minimumFractionDigits: 0 }).format(amount);

/**
 * The builder's pricing card: the supplier's price, the agent's private markup (edited through the pencil), the client
 * price.
 * @param supplierTotal The price quoted by the supplier, or null until the trip details are complete
 * @param perPerson Supplier per-person prices, used for the "per adult" line
 * @param childPrice Supplier price of one child (with bed, else without), when the party has children
 */
export const QuotePricingCard = ({
  organisationName,
  currency,
  supplierTotal,
  calculating,
  perPerson,
  childPrice,
  childCosts,
  adultCount,
  supplierPassengers,
  markupDraft,
  onEditMarkup,
}: {
  organisationName: string;
  currency: string;
  supplierTotal: number | null;
  calculating: boolean;
  perPerson?: { adult: number; cwb: number; cnb: number; infant: number };
  childPrice?: number;
  adultCount?: number;
  supplierPassengers?: ResalePassenger[];
  childCosts?: Array<Pick<ChildCost, "id" | "label" | "amount">>;
  markupDraft: MarkupDraft;
  onEditMarkup: () => void;
}) => {
  const markup = draftToMarkup(markupDraft);
  const rows = supplierPassengers || [];
  const tax = rows.reduce((sum, row) => sum + row.supplierTax * row.quantity, 0);
  const resale = rows.length && markup?.markupBasis && markup.taxBase ? calculateResalePricing(currency, rows, markup) : null;
  const breakdown = resale ? { markupAmount: resale.markup, gstAmount: resale.agentTax, clientTotal: resale.total, markupFactor: supplierTotal ? resale.total / supplierTotal : 1 } : computeMarkupBreakdown(supplierTotal ?? 0, markup, tax, currency);
  const childSellingAmount = (id: string, amount: number) => resale?.passengers.find((row) => row.key === id)?.amount ?? amount;
  const adultSellingAmount = resale?.passengers.find((row) => row.key === "adults")?.amount ?? perPerson?.adult ?? 0;
  const editLabel = markup ? "Edit markup" : "Add markup";
  return (
    <div className="rounded-xl border border-white/10 bg-(--brand-secondary) p-5 text-white shadow-[0_8px_24px_rgba(19,27,42,0.14)]">
      <h2 className="text-lg font-semibold">Pricing</h2>
      <dl className="mt-4 space-y-3 text-sm">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-white/70">Price from {organisationName}</dt>
          <dd className="tabular-nums">
            {calculating ? "Calculating…" : supplierTotal !== null ? formatMoney(supplierTotal, currency) : "—"}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="flex items-center gap-1 text-white/70">
            Your markup
            <Tooltip title={editLabel}>
              <Button
                type="text"
                size="small"
                aria-label={editLabel}
                icon={<PencilIcon size={14} />}
                onClick={onEditMarkup}
                className="!h-6 !w-6 !min-w-0 !text-white/70 hover:!bg-white/10 hover:!text-white"
              />
            </Tooltip>
          </dt>
          <dd className="tabular-nums">
            {markup && markup.markupValue > 0 ? (
              <>
                <span className="mr-2 text-white/50">
                  {markup.markupType === "percentage" ? `${markup.markupValue}%` : "Fixed"}
                </span>
                {supplierTotal !== null ? formatMoney(breakdown.markupAmount, currency) : null}
              </>
            ) : (
              <span className="text-white/50">None</span>
            )}
          </dd>
        </div>
        {markup && markup.gstPercent > 0 ? (
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-white/70">GST {markup.gstPercent}%</dt>
            <dd className="tabular-nums">{supplierTotal !== null ? formatMoney(breakdown.gstAmount, currency) : null}</dd>
          </div>
        ) : null}
      </dl>
      <div className="my-4 border-t border-white/10" />
      <div className="flex items-end justify-between gap-3">
        <span className="pb-1 text-white/70">Client price</span>
        {supplierTotal !== null ? (
          <span className="portal-title text-3xl font-semibold tabular-nums">{formatMoney(breakdown.clientTotal, currency)}</span>
        ) : (
          <span className="pb-1 text-right text-xs text-white/60">Complete the required trip details</span>
        )}
      </div>
      {supplierTotal !== null && perPerson && (perPerson.adult > 0 || (adultCount && adultSellingAmount > 0)) ? (
        <p className="mt-2 text-right text-xs text-white/60">
          ≈ {formatMoney(adultSellingAmount, currency)} per adult
          {!childCosts?.length && childPrice ? ` · child ${formatMoney(resale?.passengers.find((row) => row.key === "cwb" || row.key === "cnb")?.amount ?? childPrice, currency)}` : ""}
        </p>
      ) : null}
      {childCosts?.map((child) => <div key={child.id} className="mt-2 flex justify-between gap-3 text-xs"><span>{child.label}</span><span>{formatMoney(childSellingAmount(child.id, child.amount), currency)}</span></div>)}
    </div>
  );
};

/**
 * The builder's Actions box, laid out like the saved quote view: Save Quote, Confirm Quote and, once the quote is saved,
 * Download itinerary and Duplicate quote, with the note about what confirming does.
 */
export const QuoteActionsCard = ({
  organisationName,
  canSubmit,
  saving,
  confirming,
  onSave,
  onConfirm,
  savedQuote,
}: {
  organisationName: string;
  canSubmit: boolean;
  saving: boolean;
  confirming: boolean;
  onSave: () => void;
  onConfirm: () => void;
  /** Present once the quote has been saved: adds Download itinerary and Duplicate quote. */
  savedQuote?: {
    hasUnsavedChanges: boolean;
    downloading: boolean;
    duplicating: boolean;
    onDownload: () => void;
    onDuplicate: () => void;
  };
}) => (
  <section aria-label="Actions" className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(19,27,42,0.04)]">
    <h2 className="text-sm font-semibold text-[#131B2A]">Actions</h2>
    <div className="mt-3 flex flex-col gap-2">
      {savedQuote ? (
        <Tooltip title={savedQuote.hasUnsavedChanges ? "Save your changes first" : undefined}>
          <span className="block">
            <Button
              type="primary"
              block
              icon={<DownloadIcon size={15} />}
              loading={savedQuote.downloading}
              disabled={savedQuote.hasUnsavedChanges || saving || confirming}
              onClick={savedQuote.onDownload}
            >
              Download itinerary
            </Button>
          </span>
        </Tooltip>
      ) : null}
      <Button icon={<FileTextIcon size={15} />} loading={saving} disabled={!canSubmit || confirming} onClick={onSave}>
        Save Quote
      </Button>
      {savedQuote ? (
        <Tooltip title={savedQuote.hasUnsavedChanges ? "Save your changes first" : undefined}>
          <span className="block">
            <Button
              block
              icon={<CopyPlusIcon size={15} />}
              loading={savedQuote.duplicating}
              disabled={savedQuote.hasUnsavedChanges || saving || confirming}
              onClick={savedQuote.onDuplicate}
            >
              Duplicate quote
            </Button>
          </span>
        </Tooltip>
      ) : null}
      <Button
        type="primary"
        icon={<SendIcon size={15} />}
        loading={confirming}
        disabled={!canSubmit || saving}
        onClick={onConfirm}
      >
        Confirm Quote
      </Button>
    </div>
    <p className="mt-3 text-[11px] leading-5 text-gray-500">
      Confirming sends this quote to {organisationName} and locks every quote in this trip. Your markup stays private.
    </p>
  </section>
);
